interface RibbonStage {
  key: string;
  value: number;
  color: string;
}

interface Props {
  stages: RibbonStage[];
}

/**
 * The viewBox the ribbon is drawn in. `preserveAspectRatio="none"` stretches it to
 * the container, so the numbers only set the internal proportions.
 */
const VIEW_WIDTH = 1000;
const VIEW_HEIGHT = 224;

/** Half-height of a full-scale stage, as a share of the viewBox. */
const HEIGHT_RATIO = 0.44;

/** Bézier handle position along the segment. Higher flattens the ends of the S-curve. */
const CURVE = 0.55;

/** The solid band plus one faint halo behind it. */
const RINGS = [
  { scale: 1, opacity: 0.18 },
  { scale: 0.825, opacity: 0.83 },
];

/**
 * Enter wipe. Applied to the whole `<svg>`, not per segment: a segment clips against
 * its own bounding box, so staggering them shows four disconnected slabs mid-wipe
 * instead of one ribbon growing.
 */
const REVEAL = 'funnel-reveal 1100ms cubic-bezier(0.85, 0, 0.15, 1) both';

/**
 * One segment of the ribbon, morphing from `normStart` to `normEnd`. Both are the
 * stage value over the first stage's, so the shape is strictly to scale.
 */
const segmentPath = (normStart: number, normEnd: number, width: number, scale: number) => {
  const middle = VIEW_HEIGHT / 2;
  const startHalf = normStart * VIEW_HEIGHT * HEIGHT_RATIO * scale;
  const endHalf = normEnd * VIEW_HEIGHT * HEIGHT_RATIO * scale;
  const handle = width * CURVE;

  return [
    `M 0 ${middle - startHalf}`,
    `C ${handle} ${middle - startHalf}, ${width - handle} ${middle - endHalf}, ${width} ${middle - endHalf}`,
    `L ${width} ${middle + endHalf}`,
    `C ${width - handle} ${middle + endHalf}, ${handle} ${middle + startHalf}, 0 ${middle + startHalf}`,
    'Z',
  ].join(' ');
};

/**
 * The funnel ribbon: one tapering band across N equal-width segments, with a divider
 * on each boundary. Segment i runs from stage i's height to stage i+1's, so the entry
 * to stage j sits exactly at j/N across — which is where `OutreachConversion` puts the
 * rate pills.
 */
export const FunnelRibbon = ({ stages }: Props) => {
  const count = stages.length;
  const segmentWidth = VIEW_WIDTH / count;
  const first = stages[0]?.value ?? 0;

  // Everything is measured against the first stage. A campaign whose first stage is 0
  // has nothing to draw, and dividing by it would give NaN paths.
  const norms = stages.map((stage) => (first > 0 ? stage.value / first : 0));

  return (
    <svg
      aria-hidden="true"
      className="h-full w-full"
      preserveAspectRatio="none"
      role="presentation"
      style={{ animation: REVEAL }}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
    >
      {stages.map((stage, index) => (
        <g key={stage.key} transform={`translate(${segmentWidth * index} 0)`}>
          {RINGS.map((ring) => (
            <path
              d={segmentPath(
                norms[index],
                norms[Math.min(index + 1, count - 1)],
                segmentWidth,
                ring.scale
              )}
              fill={stage.color}
              key={ring.scale}
              opacity={ring.opacity}
            />
          ))}
        </g>
      ))}

      {stages.slice(1).map((stage, index) => (
        <line
          key={stage.key}
          opacity={0.6}
          stroke="var(--border)"
          strokeWidth={1}
          x1={segmentWidth * (index + 1)}
          x2={segmentWidth * (index + 1)}
          y1={0}
          y2={VIEW_HEIGHT}
        />
      ))}
    </svg>
  );
};
